import { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse, Row, Col } from 'reactstrap';
import { formatPrice, formatStringWithUnderscores } from 'lib/utils/formatters';

interface EarnestDepositData {
    asking_price?: string;
    builder_id?: string;
    createdAt?: string;
    customer_id?: string;
    rental_agreement?: number;
    deed_recording_percentage?: number;
    earnest_money?: string;
    id?: string;
    identity_verification_percentage?: number;
    inspection_report?: number;
    invoice_id?: string;
    offer_amount?: string;
    offer_id?: string;
    payment_method?: string;
    platform_fee_percentage?: number;
    property_id?: string;
    property_unit_id?: string;
    remainingBalance?: number;
    status?: string;
    serviceCharges?: string;
    tax_percentage?: number;
    totalExpense?: number;
    depositAmount?: number;
    rentAmount?: number;
    total_percentage?: number;
    transport_logistics_percentage?: number;
    updatedAt?: string;
    agreement: {
        agreement_type: string;
    };
}

interface InfoData {
    propertyCategory?: string;
}

interface EarnestDepositProps {
    earnestDepositData?: EarnestDepositData;
    infoData?: InfoData;
}

const EarnestDeposit = ({ earnestDepositData, infoData }: EarnestDepositProps) => {
    console.log('property category', infoData?.propertyCategory)
    const [isOpen, setIsOpen] = useState(true);

    const toggle = () => setIsOpen(!isOpen);

    // Function to get badge color based on status
    const getStatusBadgeColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-warning';
            case 'paid':
            case 'completed':
                return 'bg-success';
            case 'failed':
            case 'rejected':
                return 'bg-danger';
            case 'processing':
            case 'in_progress':
                return 'bg-info';
            default:
                return 'bg-secondary';
        }
    };

    return (
        <Card className="mb-0">
            <CardHeader onClick={toggle} style={{ cursor: 'pointer' }} className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Earnest Deposit</h6>
                <div className="d-flex align-items-center">
                    <span className={`badge ${getStatusBadgeColor(earnestDepositData?.status || '')} text-light me-2`}>{formatStringWithUnderscores(earnestDepositData?.status) || 'N/A'}</span>
                    <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} style={{ fontSize: '1.25rem' }}></i>
                </div>
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>
                    {/* Main Expenses Section */}
                    <div className="mb-3">
                        <Row className="mb-2">
                            <Col xs={8}>Inspection Report</Col>
                            <Col xs={4} className="text-end">{`$${formatPrice(earnestDepositData?.inspection_report)}`}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={8}>{earnestDepositData?.agreement?.agreement_type === 'rental_agreement' ? 'Rental Agreement' : 'Deed Recording'}</Col>
                            <Col xs={4} className="text-end">{`${earnestDepositData?.agreement?.agreement_type === 'rental_agreement' ? earnestDepositData?.rental_agreement : earnestDepositData?.deed_recording_percentage}%`}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={8}>Taxes & Duty</Col>
                            <Col xs={4} className="text-end">{`${earnestDepositData?.tax_percentage}%`}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={8}>Identity Verification</Col>
                            <Col xs={4} className="text-end">{`${earnestDepositData?.identity_verification_percentage}%`}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={8}>Transport & Logistics</Col>
                            <Col xs={4} className="text-end">{`${earnestDepositData?.transport_logistics_percentage}%`}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col xs={8}>RealWealtdy Fees</Col>
                            <Col xs={4} className="text-end">{`${earnestDepositData?.platform_fee_percentage}%`}</Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={8}><strong>Total</strong></Col>
                            <Col xs={4} className="text-end"><strong>{`${earnestDepositData?.total_percentage}%`}</strong></Col>
                        </Row>
                    </div>

                    {/* Divider */}
                    <hr className="my-3" />

                    {/* Summary Section */}
                    <div className="mb-0">
                        <Row className="mb-2">
                            <Col xs={8}>Service Chagres</Col>
                            <Col xs={4} className="text-end">{`$${formatPrice(infoData?.propertyCategory === 'Sell'? earnestDepositData?.earnest_money : earnestDepositData?.serviceCharges)}`}</Col>
                        </Row>
                        {infoData?.propertyCategory === 'Rent' &&
                        <Row className="mb-2">
                            <Col xs={8}>Deposit Amount</Col>
                            <Col xs={4} className="text-end">{earnestDepositData?.depositAmount ? `$${formatPrice(earnestDepositData?.depositAmount)}` : 'N/A'}</Col>
                        </Row>}
                        {infoData?.propertyCategory === 'Rent' &&
                        <Row className="mb-2">
                            <Col xs={8}>Rent Amount</Col>
                            <Col xs={4} className="text-end">{earnestDepositData?.rentAmount ? `$${formatPrice(earnestDepositData?.rentAmount)}` : 'N/A'}</Col>
                        </Row>}
                        {infoData?.propertyCategory === 'Rent' &&
                        <Row className="mb-2">
                            <Col xs={8}><strong>Total Paid</strong></Col>
                            <Col xs={4} className="text-end"><strong>{earnestDepositData?.earnest_money ? `$${formatPrice(earnestDepositData?.earnest_money)}` : 'N/A'}</strong></Col>
                        </Row>}
                        {infoData?.propertyCategory === 'Sell' &&
                        <Row className="mb-2">
                            <Col xs={8}>Total Expense</Col>
                            <Col xs={4} className="text-end">{earnestDepositData?.totalExpense ? `$${formatPrice(earnestDepositData?.totalExpense)}` : 'N/A'}</Col>
                        </Row>}
                        {infoData?.propertyCategory === 'Sell' &&
                        <Row className="mb-3">
                            <Col xs={8}>Remaining Balance</Col>
                            <Col xs={4} className="text-end">{earnestDepositData?.remainingBalance ? `$${formatPrice(earnestDepositData?.remainingBalance)}` : 'N/A'}</Col>
                        </Row>}
                    </div>

                    {/* Divider */}
                    {/* <hr className="my-3" /> */}

                    {/* Payment Status Section */}
                    {/* <div className="mb-2">
                        <Row>
                            <Col xs={8}>Payment Status</Col>
                            <Col xs={4} className="text-end">
                                <span className={`badge ${getStatusBadgeColor(earnestDepositData?.status || '')} text-light`}>
                                    {formatStatus(earnestDepositData?.status)}
                                </span>
                            </Col>
                        </Row>
                    </div> */}
                </CardBody>
            </Collapse>
        </Card>
    );
};

export default EarnestDeposit;